import { useI18n } from '@/lib/i18n'
import type { EventConfig } from '@/types'
import { Palette, Type, Image, Upload } from 'lucide-react'
import { uploadFile } from '@/lib/utils'

const GOOGLE_FONTS = [
  { label: 'Inter', value: 'Inter, sans-serif' },
  { label: 'Outfit', value: 'Outfit, sans-serif' },
  { label: 'Poppins', value: 'Poppins, sans-serif' },
  { label: 'Playfair Display', value: '"Playfair Display", serif' },
  { label: 'Merriweather', value: 'Merriweather, serif' },
  { label: 'Space Grotesk', value: '"Space Grotesk", sans-serif' },
  { label: 'DM Sans', value: '"DM Sans", sans-serif' },
  { label: 'Oswald', value: 'Oswald, sans-serif' },
  { label: 'Bebas Neue', value: '"Bebas Neue", cursive' },
  { label: 'Roboto Mono', value: '"Roboto Mono", monospace' },
]

function ColorField({ label, description, value, onChange }: { label: string; description?: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-foreground">{label}</label>
      {description && <p className="text-xs text-muted-foreground">{description}</p>}
      <div className="flex items-center gap-3">
        <label className="relative cursor-pointer">
          <div
            className="h-12 w-12 rounded-xl border-2 border-border shadow-inner cursor-pointer transition-transform hover:scale-105"
            style={{ backgroundColor: value }}
          />
          <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="absolute inset-0 h-full w-full cursor-pointer opacity-0" />
        </label>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm font-mono text-foreground focus:border-primary/50 transition-colors"
          maxLength={7}
        />
      </div>
    </div>
  )
}

export default function BrandingSettings({ event, onChange }: { event: EventConfig; onChange: (u: Partial<EventConfig>) => void }) {
  const { t } = useI18n()
  const { branding } = event

  const update = (key: keyof typeof branding, value: unknown) => {
    onChange({ branding: { ...branding, [key]: value } })
  }

  return (
    <div className="space-y-8 max-w-2xl">
      <div>
        <h2 className="text-xl font-bold">{t('branding.title')}</h2>
        <p className="text-sm text-muted-foreground mt-1">{t('branding.description')}</p>
      </div>
      {/* Colors */}
      <div className="rounded-xl border border-border bg-card/50 p-5 space-y-5">
        <div className="flex items-center gap-2 font-semibold text-sm">
          <Palette className="h-4 w-4 text-primary" /> {t('branding.colors')}
        </div>
        <div className="grid grid-cols-2 gap-6">
          <ColorField
            label={t('branding.primary')}
            description={t('branding.primaryDesc')}
            value={branding.primaryColor}
            onChange={(v) => update('primaryColor', v)}
          />
          <ColorField
            label={t('branding.accent')}
            description={t('branding.accentDesc')}
            value={branding.accentColor}
            onChange={(v) => update('accentColor', v)}
          />
        </div>

        {/* Preview swatches */}
        <div className="flex gap-2 pt-1">
          <div className="flex-1 rounded-lg py-2.5 text-center text-xs font-semibold text-white" style={{ backgroundColor: branding.primaryColor }}>
            {t('branding.primary')}
          </div>
          <div className="flex-1 rounded-lg py-2.5 text-center text-xs font-semibold text-white" style={{ backgroundColor: branding.accentColor }}>
            {t('branding.accent')}
          </div>
        </div>
      </div>

      {/* Font */}
      <div className="rounded-xl border border-border bg-card/50 p-5 space-y-4">
        <div className="flex items-center gap-2 font-semibold text-sm">
          <Type className="h-4 w-4 text-purple-400" /> {t('branding.fontFamily')}
        </div>
        <div className="grid grid-cols-2 gap-2">
          {GOOGLE_FONTS.map((font) => (
            <button
              key={font.value}
              onClick={() => update('fontFamily', font.value)}
              className={`flex flex-col items-start rounded-lg border px-3 py-2.5 text-left transition-all ${
                branding.fontFamily === font.value
                  ? 'border-primary/40 bg-primary/10 text-primary'
                  : 'border-border hover:bg-muted text-muted-foreground'
              }`}
            >
              <span className="text-xs font-medium">{font.label}</span>
              <span className="text-lg leading-tight" style={{ fontFamily: font.value }}>Aa Bb 123</span>
            </button>
          ))}
        </div>
      </div>

      {/* Logo */}
      <div className="rounded-xl border border-border bg-card/50 p-5 space-y-4">
        <div className="flex items-center gap-2 font-semibold text-sm">
          <Image className="h-4 w-4 text-amber-400" /> {t('branding.logo')}
        </div>
        <label className="flex items-center gap-3 rounded-lg border border-dashed border-border bg-background px-4 py-3 hover:border-primary/40 hover:bg-muted cursor-pointer transition-colors">
          <Upload className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">{branding.logoAsset ? t('branding.replaceLogo') : t('branding.uploadLogo')}</span>
          <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
            const file = e.target.files?.[0]
            if (file) {
              const url = await uploadFile(file)
              if (url) update('logoAsset', url)
            }
          }} />
        </label>
        {branding.logoAsset && (
          <div className="flex items-center gap-4 rounded-xl border border-border bg-muted p-4">
            <img src={branding.logoAsset} alt="Logo" className="h-16 object-contain" />
            <div className="text-xs text-muted-foreground">{t('branding.preview')}</div>
            <button
              onClick={() => update('logoAsset', null)}
              className="ml-auto text-xs text-destructive hover:text-destructive/80"
            >
              {t('branding.remove')}
            </button>
          </div>
        )}
      </div>

      {/* Background */}
      <div className="rounded-xl border border-border bg-card/50 p-5 space-y-4">
        <div className="flex items-center gap-2 font-semibold text-sm">
          <Image className="h-4 w-4 text-emerald-400" /> {t('branding.background')}
        </div>
        <label className="flex items-center gap-3 rounded-lg border border-dashed border-border bg-background px-4 py-3 hover:border-primary/40 hover:bg-muted cursor-pointer transition-colors">
          <Upload className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">{branding.backgroundAsset ? t('branding.replaceBg') : t('branding.uploadBg')}</span>
          <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
            const file = e.target.files?.[0]
            if (file) {
              const url = await uploadFile(file)
              if (url) update('backgroundAsset', url)
            }
          }} />
        </label>
        {branding.backgroundAsset && (
          <div className="relative rounded-xl overflow-hidden border border-border h-36">
            <img src={branding.backgroundAsset} alt="Background" className="h-full w-full object-cover" />
            <button
              onClick={() => update('backgroundAsset', null)}
              className="absolute top-2 right-2 rounded-lg bg-black/60 px-2 py-1 text-xs text-white hover:bg-black/80"
            >
              {t('branding.remove')}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
